import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pendingWrites } from './api';

/*
 * Aggiornamento automatico (04/11/2026): dopo una pubblicazione del gestionale chi ha la pagina aperta da ore
 * continuava a usare la versione vecchia (caso Narciso, correzione «Failed to fetch»). Ora la pagina si
 * aggiorna da sola, ma solo in momenti sicuri: al cambio di sezione (lo stato della schermata si perderebbe
 * comunque) oppure nell'editor contenuti dopo 10 minuti fermo (lì le modifiche sono già salvate sul server).
 * Mai se una schermata ha segnalato modifiche non salvate.
 */
const CHECK_EVERY_MS = 5 * 60_000;
const IDLE_MS = 10 * 60_000;
const unsaved = new Set<string>();
let newVersion = false;
let lastActivity = Date.now();
let reloading = false;

/** Vero quando è l'aggiornamento automatico a ricaricare: gli avvisi «vuoi uscire?» non devono comparire. */
export function isAutoReloading(): boolean {
  return reloading;
}

/** Le schermate con modifiche non salvate si registrano qui: finché ci sono, niente aggiornamento automatico. */
export function markUnsaved(key: string, value: boolean) {
  if (value) unsaved.add(key);
  else unsaved.delete(key);
}

function currentBundle(): string {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  return script ? new URL(script.src, location.href).pathname : '';
}

async function checkVersion() {
  const mine = currentBundle();
  if (!mine || newVersion) return;
  try {
    const html = await fetch('/', { cache: 'no-store', credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : ''));
    const live = html.match(/\/assets\/index-[^"']+\.js/)?.[0];
    if (live && live !== mine) newVersion = true;
  } catch {
    // Senza rete si riprova al giro dopo.
  }
}

function reloadIfSafe() {
  if (!newVersion || unsaved.size || pendingWrites() > 0) return false;
  reloading = true;
  location.reload();
  return true;
}

export function AutoUpdate() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Si aspetta un attimo: la schermata appena chiusa può avere ancora un salvataggio in viaggio.
    const changedAt = Date.now();
    // Se nel frattempo si è già cominciato a scrivere, si aspetta il prossimo momento sicuro.
    const timer = window.setTimeout(() => { if (lastActivity <= changedAt + 300) reloadIfSafe(); }, 1200);
    return () => window.clearTimeout(timer);
  }, [pathname, search]);
  useEffect(() => {
    const touch = () => { lastActivity = Date.now(); };
    const onVisible = () => { if (document.visibilityState === 'visible') void checkVersion(); };
    const events = ['pointerdown', 'keydown', 'input', 'wheel', 'touchstart'] as const;
    events.forEach((name) => window.addEventListener(name, touch, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      void checkVersion().then(() => { if (location.pathname.includes('/editor/') && Date.now() - lastActivity > IDLE_MS) reloadIfSafe(); });
    }, CHECK_EVERY_MS);
    void checkVersion();
    return () => {
      events.forEach((name) => window.removeEventListener(name, touch));
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);
  return null;
}
